import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  PSPTask,
  TaskParam,
  pspTasksCounter,
} from 'src/app/_models/PSP/psp-task.model';
import { PspTasksService } from 'src/app/_services/PSP/psp-tasks.service';
import { LookupModalService } from 'src/app/_services/PSP/modals.service';
import { IsPermissionExist } from 'src/app/helpers/get-permissions';
import {
  PspEvaPermission,
  PspLillyPermission,
  PspRochePermission,
} from 'src/app/_constants/permissions.constant';
import {  DatatableComponent, TableColumn } from '@swimlane/ngx-datatable';
import { ISmartTableMessage } from 'src/app/_interfaces/smart-table/i-message';
import { DatePipe } from '@angular/common';
import { ISmartTablePagination } from 'src/app/_interfaces/smart-table/i-pagination';
import { Router } from '@angular/router';
import { PspCompanyEnum } from 'src/app/enums/psp-company.enum';
import { FilesService } from 'src/app/_services/files/files.service';
import { PspTypeEnum } from 'src/app/enums/psp-type.enum';

@Component({
  selector: 'app-daily-tasks',
  templateUrl: './daily-tasks.component.html',
  styleUrls: ['./daily-tasks.component.scss'],
})
export class DailyTasksComponent implements OnInit {
  tHeads = {
    0: [
      'Task Type',
      'User Name',
      'Program Name',
      'Company Name',
      'Submission Date & Time',
    ],
    1: ['User Name', 'Program Name', 'Company Name', 'Submission Date & Time'],
    2: [
      'Caregiver Name',
      'Patient Name',
      'Program Name',
      'Company Name',
      'Submission Date & Time',
    ],
    3: ['User Name', 'Program Name', 'Company Name', 'Submission Date & Time'],
    // roche headers
    roche: [
      'Patient Enrollment ID',
      'Program Name',
      'Company Name',
      'Doctor Name',
      'Doctor Code',
      'Submission Date & Time',
      'Attached Rx',
    ],
    all: [
      'Task Type',
      'User Name',
      'Program Name',
      'Company Name',
      'Patient Enrollment ID',
      'Doctor Name',
      'Doctor Code',
      'Submission Date & Time',
      'Attached Rx',
    ],
  };

  theadArray = ['Task Type', 'User Name', 'Program Name', 'Submission Date & Time'];

  pspTasksCounter: pspTasksCounter = {
    all: 0,
    preRegistrationPatients: 0,
    pendingPatients: 0,
    pendingCaregivers: 0,
  };
  tasksArray: PSPTask[];
  totalNumbersOfTasks = {
    0: 0,
    1: 0,
    2: 0,
    3: 0,
  };
  apiParams: TaskParam = {
    currentPage: 1,
    pageSize: 10,
    patientSubscriptionTypeId: 0,
  };
  eligiblePrograms: string[] = [];
  totalItems: number;
  columns: TableColumn[];
  enrollmentIdSearch: string = '';
  isAllRochePatientData: boolean = false;
  isAtLeastOneRocheData: boolean = false;

  @ViewChild('patientNameRef') public patientNameRef: TemplateRef<any>;
  @ViewChild('enrollmentIdRef') public enrollmentIdRef: TemplateRef<any>;
  @ViewChild('attachedRxRef') public attachedRxRef: TemplateRef<any>;

  messages: ISmartTableMessage = {
    emptyMessage: 'Hmm, There are no daily tasks',
    totalMessage: 'total',
    selectedMessage: '',
  };
  currentSearchValue: string = '';
  hasRochePermission: boolean = false;

  constructor(
    private pspTasksService: PspTasksService,
    private spinner: NgxSpinnerService,
    private myModalService: LookupModalService,
    private datePipe: DatePipe,
    private router: Router,
    private filesService: FilesService
  ) {}

  ngOnInit(): void {
    this.hasRochePermission = IsPermissionExist(PspRochePermission)
      ? true
      : false;
    this.getEligiblePrograms();
    this.onSearch(0);
  }

  onSearch(subscriptionId, currentPage: number = 1, pspCode: string = null) {
    this.spinner.show();
    this.apiParams.currentPage = currentPage;
    this.apiParams.patientSubscriptionTypeId = subscriptionId;
    if (pspCode && pspCode.trim() !== '') {
      this.apiParams.pspCode = pspCode.trim();
      this.currentSearchValue = pspCode.trim();
    } else {
      delete this.apiParams.pspCode;
      this.currentSearchValue = '';
    }
    this.pspTasksService.getAllTasks(this.apiParams).subscribe((res) => {
      this.tasksArray = res.data?.items;
      this.totalItems = res.data.pagination.totalItems;
      this.totalNumbersOfTasks[subscriptionId] = this.tasksArray.length;
      this.pspTasksCounter = res.data?.pspTasksCounter;

      // Determine header configuration based on data context
      this.determineHeaderConfiguration();

      // Configure table columns based on the determined headers
      this.configureTableColumns();

      this.spinner.hide();
    });
  }

  private determineHeaderConfiguration(): void {
    const isRocheFiltered = this.apiParams.companyId === PspCompanyEnum.Roche;
    const isAllCompanies = this.apiParams.companyId === 0;

    // Check if all data is Roche
    this.isAllRochePatientData =
      this.tasksArray?.every(
        (task) =>
          task.program?.companyId === PspCompanyEnum.Roche &&
          task.typeId === PspTypeEnum.PspPatient &&
          task.patient?.pspCode != null
      ) || false;
    // Check at least one data is Roche, to remove roche columns from the table if there is no roche data
    this.isAtLeastOneRocheData =
      this.tasksArray?.some(
        (task) =>
          task.program?.companyId === PspCompanyEnum.Roche &&
          task.typeId === PspTypeEnum.PspPatient
      ) || false;

    if (
      (isRocheFiltered && this.isAllRochePatientData) ||
      (isAllCompanies && this.isAllRochePatientData)
    ) {
      // Use Roche-specific headers
      this.theadArray = this.tHeads.roche;
    } else if (this.isAtLeastOneRocheData) {
      // Use mixed data headers
      this.theadArray = this.tHeads.all;
    } else {
      // Use default subscription-based headers
      this.theadArray = this.tHeads[this.apiParams.patientSubscriptionTypeId];
    }
  }

  private configureTableColumns(): void {
    // Create column configuration based on the determined headers
    this.columns = this.theadArray.map((header) => {
      switch (header) {
        case 'Task Type':
          return {
            prop: 'typeId',
            name: 'Task Type',
            pipe: {
              transform: (val) =>
                val == 1 ? 'Pending Patient' : 'Pending CareReceiver',
            },
          };
        case 'User Name':
          return {
            prop: 'patient.name',
            name: 'User Name',
            cellTemplate: this.patientNameRef,
          };
        case 'Caregiver Name':
          return {
            prop: 'careReceiver.name',
            name: 'Caregiver Name',
            cellTemplate: this.patientNameRef,
          };
        case 'Patient Name':
          return {
            prop: 'patient.name',
            name: 'Patient Name',
            cellTemplate: this.patientNameRef,
          };
        case 'Program Name':
          return { prop: 'program.name', name: 'Program Name' };
        case 'Company Name':
          return { prop: 'program.companyName', name: 'Company Name' };
        case 'Patient Enrollment ID':
          return {
            prop: 'patient.pspCode',
            name: 'Patient Enrollment ID',
            minWidth: 200,
          }
        case 'Doctor Name':
          return { prop: 'doctor.name', name: 'Doctor Name' };
        case 'Doctor Code':
          return { prop: 'doctor.code', name: 'Doctor Code' };
        case 'Submission Date & Time':
          return {
            prop: 'creationDate',
            name: 'Submission Date & Time',
            pipe: {
              transform: (val) =>
                this.datePipe.transform(val, "MMM d, y 'at' h:mm a"),
            },
            width: 260,
          };
        case 'Attached Rx':
          return {
            prop: 'doctor.rxUrl',
            name: 'Attached Rx',
            cellTemplate: this.attachedRxRef,
            width: 220,
            sortable: false,
          };
        default:
          return {
            prop: header.toLowerCase().replace(/\s+/g, ''),
            name: header,
          };
      }
    });
  }

  openModal() {
    this.myModalService.openChooseCstModal();
  }

  getEligiblePrograms() {
    if (
      IsPermissionExist(PspEvaPermission) &&
      IsPermissionExist(PspLillyPermission)
    ) {
      this.eligiblePrograms = this.eligiblePrograms.concat([
        'All Programs',
        'Eva Programs',
        'Lilly Programs',
      ]);
      this.apiParams.companyId = 0;
    }
  }

  onChangePage(pageDetails: ISmartTablePagination) {
    this.onSearch(
      this.apiParams.patientSubscriptionTypeId,
      pageDetails.offset + 1
    );
  }

  routeToPspDetails(pspTask) {
    if (
      pspTask.selected[0].doctor.attachedRxUrl != null ||
      pspTask.selected[0].doctor.attachedRxUrl != null ||
      pspTask.selected[0].patient.name == null
    ) {
      this.router.navigate([
        '/psp',
        'add-roche-patient',
        pspTask.selected[0].id,
      ]);
    } else if (pspTask.selected[0].typeId == 1)
      this.router.navigate(['/psp', 'pending-patient', pspTask.selected[0].id]);
    else if (pspTask.selected[0].typeId == 2)
      this.router.navigate([
        '/psp',
        'pending-receiver',
        pspTask.selected[0].id,
      ]);
    else this.router.navigate(['/psp']);
  }

  onCompanySelected(companyId: number): void {
    this.apiParams.currentPage = 1;
    this.apiParams.companyId = companyId;
    this.onSearch(this.apiParams.patientSubscriptionTypeId);
  }

  onEnrollmentIdSearch(pspCode: string): void {
    this.apiParams.currentPage = 1;

    this.onSearch(
      this.apiParams.patientSubscriptionTypeId,
      this.apiParams.currentPage,
      pspCode
    );
  }

  clearEnrollmentIdSearch(): void {
    this.onSearch(
      this.apiParams.patientSubscriptionTypeId,
      this.apiParams.currentPage
    );
  }

  viewAttachedRx(task: PSPTask, event: any): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const fileUrl = task.doctor?.rxUrl;
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }

  downloadAttachedRx(task: PSPTask, event: any): void {
    event.preventDefault();
    event.stopPropagation();

    const fileUrl = task.doctor?.rxUrl;
    if (fileUrl) {
      this.filesService.downloadFile(fileUrl);
    }
  }
}
